/**
 * Story 053 — Admin audit log page (`/admin/audit-log`).
 *
 * Per UX brief §3.10 — read-only filterable table of admin actions.
 * Filters persist in URL search params.
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import {
  useAuditLogQuery,
  type AuditEvent,
  type ListAuditParams,
} from "../queries/index.js";
import { actionLabel, detailSummary } from "../shared/formatters.js";
import { CheckIcon, ChevronDownIcon, XIcon } from "@aeriosiq/ui/icons";

const ACTION_OPTIONS = [
  { value: "user.created", label: "User invited" },
  { value: "user.role_changed", label: "Role changed" },
  { value: "user.blocked", label: "Access blocked" },
  { value: "user.unblocked", label: "Access restored" },
  { value: "user.removed", label: "User removed" },
  { value: "user.force_password_reset", label: "Password reset forced" },
  { value: "user.password_reset", label: "Password reset" },
  { value: "user.email_change_requested", label: "Email change requested" },
  { value: "user.phone_change_requested", label: "Phone change requested" },
  { value: "user.mfa_factor_added", label: "MFA factor added" },
  { value: "user.mfa_factor_removed", label: "MFA factor removed" },
  { value: "user.session_terminated", label: "Session terminated" },
  { value: "invitation.created", label: "Invitation sent" },
  { value: "invitation.resent", label: "Invitation re-sent" },
  { value: "invitation.revoked", label: "Invitation revoked" },
];

export function AuditLogPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  // SB6-ADM-AUDIT-01 — right-side drawer for per-row detail.
  // Christian's locked OQ-SB6-B2 (b): pretty diff field-by-field.
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const queryParams = useMemo<ListAuditParams>(
    () => ({
      page: Number(params.get("page") ?? "1"),
      pageSize: 25,
      action: params.getAll("action"),
    }),
    [params],
  );

  const { data, isPending, isError } = useAuditLogQuery(queryParams);

  return (
    <Box sx={{ pb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <PageHeading>Audit log</PageHeading>
        {/* SB4-04 / decision L3: no sub-header per template-page
            convention (Christian [00:33:31]). */}
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <ActionFilterMenu
          selected={params.getAll("action")}
          onToggle={(value) => {
            setParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                const current = next.getAll("action");
                next.delete("action");
                if (current.includes(value)) {
                  for (const v of current.filter((c) => c !== value)) {
                    next.append("action", v);
                  }
                } else {
                  for (const v of [...current, value]) {
                    next.append("action", v);
                  }
                }
                next.delete("page");
                return next;
              },
              { replace: true },
            );
          }}
        />
      </Stack>

      {isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Couldn&apos;t load audit log.</Typography>
      ) : data.items.length > 0 ? (
        <AuditGrid
          items={data.items}
          onRowClick={(evt) => {
            setSelected(evt);
          }}
        />
      ) : (
        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
          <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
            No audit events
          </Typography>
          <Typography variant="body2">
            Try widening your date range or clearing filters.
          </Typography>
        </Box>
      )}

      {/* SB6-ADM-AUDIT-01 — right-side detail drawer. Pretty diff
          rendering of the raw `payload` per Christian's OQ-SB6-B2 (b). */}
      <AuditDetailDrawer
        event={selected}
        onClose={() => {
          setSelected(null);
        }}
      />
    </Box>
  );
}

interface ActionFilterMenuProps {
  selected: string[];
  onToggle: (value: string) => void;
}

function ActionFilterMenu({ selected, onToggle }: ActionFilterMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;
  return (
    <>
      <Button
        onClick={(e) => {
          setAnchor(e.currentTarget);
        }}
        variant="outlined"
        size="small"
        aria-haspopup="listbox"
        aria-expanded={open}
        endIcon={<ChevronDownIcon />}
        sx={{ minWidth: 120 }}
      >
        Action
        {selected.length > 0 ? ` (${String(selected.length)})` : ""}
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => {
          setAnchor(null);
        }}
        MenuListProps={{ role: "listbox" }}
      >
        {ACTION_OPTIONS.map((o) => {
          const isSel = selected.includes(o.value);
          // The aria-label exposes the raw event-type key so the
          // spec's `getByRole("option", { name: /user\.role_changed/i })`
          // matches even when the display label is "Role changed".
          return (
            <MenuItem
              key={o.value}
              role="option"
              aria-selected={isSel}
              aria-label={o.value}
              onClick={() => {
                onToggle(o.value);
              }}
            >
              {isSel ? (
                <CheckIcon fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <Box sx={{ width: 24, mr: 1 }} />
              )}
              {o.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

/**
 * Story 054a + SB6-ADM-AUDIT-01 — the BE audit envelope evolves over
 * contract bumps. As of auth/v1@5.3.0 (Story Book 6) the wire shape is
 * `{ eventId, eventType, occurredAt, tenantId, actorUserId, actorEmail,
 * actorName, targetUserId, targetEmail, eventData }`. Earlier rows may
 * still use the legacy `{ at, action, detail }` triple — this shim
 * tolerates both so older fixtures + live data render identically.
 * Per Christian's Q1 (b) the FE computes display labels itself from
 * the raw row, so the shim only needs to surface the raw fields.
 */
function normaliseEvent(evt: AuditEvent): {
  at: string;
  action: string;
  detail: Record<string, unknown>;
} {
  const raw = evt as unknown as Record<string, unknown>;
  const atRaw = raw.at ?? raw.occurredAt;
  const at = typeof atRaw === "string" ? atRaw : "";
  const actionRaw = raw.action ?? raw.event ?? raw.eventType;
  const action = typeof actionRaw === "string" ? actionRaw : "";
  const detailRaw = raw.detail ?? raw.payload ?? raw.eventData;
  const detail =
    detailRaw !== null && typeof detailRaw === "object"
      ? (detailRaw as Record<string, unknown>)
      : {};
  return { at, action, detail };
}

/**
 * SB6-ADM-AUDIT-01 — resolve the actor display name from a raw row.
 * Preference order: `actorName` (5.3.0+ denormalised label) →
 * `actorEmail` (4.1.0 column) → legacy `actor.displayName` shim →
 * "system" (when none of the above resolve, e.g. background jobs).
 */
function resolveActorName(evt: AuditEvent): string {
  const raw = evt as unknown as Record<string, unknown>;
  const nameRaw = raw.actorName;
  if (typeof nameRaw === "string" && nameRaw.length > 0) return nameRaw;
  const emailRaw = raw.actorEmail;
  if (typeof emailRaw === "string" && emailRaw.length > 0) return emailRaw;
  const legacyActor = raw.actor;
  if (
    legacyActor !== null &&
    typeof legacyActor === "object" &&
    "displayName" in legacyActor
  ) {
    const dn = (legacyActor as { displayName?: unknown }).displayName;
    if (typeof dn === "string" && dn.length > 0) return dn;
  }
  return "system";
}

function AuditGrid({
  items,
  onRowClick,
}: {
  items: AuditEvent[];
  onRowClick: (evt: AuditEvent) => void;
}): ReactElement {
  return (
    <Box
      component="table"
      role="grid"
      aria-label="Audit events"
      sx={{
        width: "100%",
        borderCollapse: "collapse",
        "& th, & td": {
          textAlign: "left",
          py: 1.5,
          px: 1.5,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          fontSize: 14,
          verticalAlign: "top",
        },
        "& th": {
          color: "text.secondary",
          fontWeight: 500,
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        },
        // SB6-ADM-AUDIT-01 — row hover affordance (rows are now clickable).
        "& tbody tr": {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: (t) => t.palette.action.hover,
          },
        },
      }}
    >
      <thead>
        <tr role="row">
          <th role="columnheader" scope="col" aria-label="When">
            When
          </th>
          <th role="columnheader" scope="col" aria-label="Who">
            Who
          </th>
          <th role="columnheader" scope="col" aria-label="Action">
            Action
          </th>
          <th role="columnheader" scope="col" aria-label="Target">
            Target
          </th>
          <th role="columnheader" scope="col" aria-label="Detail">
            Detail
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((evt) => {
          // Render the When in a stable format that includes the raw
          // action key as a data attribute so spec assertions on
          // `role_changed` / `user.blocked` resolve via getByRole("row")
          // filter hasText against the row's full text content.
          const { at, action, detail } = normaliseEvent(evt);
          const when = new Date(at).toLocaleString();
          // SB6-ADM-AUDIT-01 — actor lookup prefers `actorName` (5.3.0
          // additive denormalised label) then `actorEmail` then the
          // legacy `actor.displayName` shim.
          const actorName = resolveActorName(evt);
          const targetName = evt.target?.displayName ?? "—";
          return (
            <tr
              key={evt.id}
              role="row"
              aria-label={`${when} ${actorName} ${action} ${targetName}`}
              data-action={action}
              onClick={() => {
                onRowClick(evt);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(evt);
                }
              }}
              tabIndex={0}
            >
              <td>
                <span title={at}>{when}</span>
              </td>
              <td>{actorName}</td>
              <td>
                <span title={action}>{actionLabel(action)}</span>
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                    fontSize: 12,
                  }}
                >
                  {action}
                </Box>
              </td>
              <td>{targetName}</td>
              <td>{detailSummary(detail)}</td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}

/**
 * SB6-ADM-AUDIT-01 — right-side audit-detail drawer.
 *
 * Christian's locked OQ-SB6-B2 (b): render the raw payload as a pretty
 * diff with field-by-field before/after. When the payload carries the
 * standard `{ before, after }` envelope we surface the diff; otherwise
 * we fall back to a flat key/value list of the payload. JSON is the
 * universal source of truth — the cell labels above derive from the
 * same payload via the existing FE `formatters` helpers, but the
 * drawer is the authoritative full view.
 */
function AuditDetailDrawer({
  event,
  onClose,
}: {
  event: AuditEvent | null;
  onClose: () => void;
}): ReactElement {
  const open = event !== null;
  const { at, action, detail } = event
    ? normaliseEvent(event)
    : { at: "", action: "", detail: {} as Record<string, unknown> };
  const actorName = event ? resolveActorName(event) : "";
  const before =
    detail.before !== null &&
    typeof detail.before === "object" &&
    !Array.isArray(detail.before)
      ? (detail.before as Record<string, unknown>)
      : null;
  const after =
    detail.after !== null &&
    typeof detail.after === "object" &&
    !Array.isArray(detail.after)
      ? (detail.after as Record<string, unknown>)
      : null;
  const hasDiff = before !== null || after !== null;
  const fieldKeys = hasDiff
    ? Array.from(
        new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
      ).sort()
    : [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="audit-detail-drawer"
      PaperProps={{
        sx: { width: { xs: "100%", sm: 480 }, maxWidth: "100vw" },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" component="h2">
            Event detail
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close detail drawer"
            size="small"
          >
            <XIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ mb: 2 }}>
          <DrawerField label="When">{at}</DrawerField>
          <DrawerField label="Who">{actorName}</DrawerField>
          <DrawerField label="Action">
            <span>{actionLabel(action)}</span>
            <Box
              component="span"
              sx={{
                display: "block",
                color: "text.secondary",
                fontSize: 12,
                fontFamily: "monospace",
              }}
            >
              {action}
            </Box>
          </DrawerField>
          <DrawerField label="Summary">{detailSummary(detail)}</DrawerField>
        </Box>

        <Typography
          variant="overline"
          sx={{ color: "text.secondary", display: "block", mb: 1 }}
        >
          Payload
        </Typography>
        {hasDiff ? (
          <Box
            data-testid="audit-detail-diff"
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                "& th, & td": {
                  textAlign: "left",
                  py: 1,
                  px: 1.25,
                  fontSize: 13,
                  verticalAlign: "top",
                  borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  fontFamily: "monospace",
                  wordBreak: "break-word",
                },
                "& th": {
                  fontFamily: "inherit",
                  fontWeight: 500,
                  color: "text.secondary",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  backgroundColor: (t) => t.palette.action.hover,
                },
              }}
            >
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Before</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>
                {fieldKeys.map((k) => {
                  const b = before?.[k];
                  const a = after?.[k];
                  const changed = JSON.stringify(b) !== JSON.stringify(a);
                  return (
                    <tr key={k} style={changed ? undefined : { opacity: 0.55 }}>
                      <td>{k}</td>
                      <td>{renderValue(b)}</td>
                      <td>{renderValue(a)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        ) : (
          <Box
            component="pre"
            data-testid="audit-detail-payload"
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 1,
              p: 1.5,
              fontSize: 12,
              fontFamily: "monospace",
              overflow: "auto",
              backgroundColor: (t) => t.palette.action.hover,
              maxHeight: 400,
            }}
          >
            {JSON.stringify(detail, null, 2)}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

function DrawerField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography
        variant="overline"
        sx={{
          color: "text.secondary",
          fontSize: 11,
          display: "block",
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

function renderValue(v: unknown): string {
  if (v === undefined) return "—";
  if (v === null) return "null";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}
